/**
 * documentSearch.js — Find-in-document search over a whole PDF, independent
 * of any mounted text layer.
 *
 * ScrollablePages virtualizes rendering to visible±1 pages, so a DOM text
 * layer (PdfTextLayer) is never mounted for the whole document at once — it
 * can't power a document-wide search. This lib instead walks
 * `pdfDoc.getPage(n).getTextContent()` page-at-a-time, independent of what's
 * currently rendered.
 *
 * Rect math mirrors contentDetector.js: item x = tx[4], top-left y =
 * pageHeight - tx[5] - fontSize, fontSize = hypot(tx[2],tx[3]) (falls back to
 * hypot(tx[0],tx[1]) or 12). A match's rect.x is prorated by character offset
 * over the item's total width (`x + (start/str.length) * item.width`) — a v1
 * approximation that assumes uniform glyph width within the item. It's
 * imprecise for proportional fonts (a run of "i"s vs "W"s won't be evenly
 * spaced) but good enough for a highlight rectangle; a future pass could use
 * per-glyph advances if PDF.js exposes them cheaply.
 */

// Combining diacritical marks block — stripped after NFD normalization so
// "café" normalizes down to "cafe" for matching.
const DIACRITICS_RE = /[̀-ͯ]/g;

function normalize(str) {
  return str.normalize('NFD').replace(DIACRITICS_RE, '').toLowerCase();
}

/**
 * Find all occurrences of `needle` in `haystack`. Case-sensitivity is the
 * caller's responsibility (pass already-cased strings).
 */
function findAllOffsets(haystack, needle) {
  const offsets = [];
  if (!needle) return offsets;
  let from = 0;
  for (;;) {
    const idx = haystack.indexOf(needle, from);
    if (idx === -1) break;
    offsets.push(idx);
    from = idx + needle.length;
  }
  return offsets;
}

/**
 * Compute a single text item's page-coordinate geometry, mirroring
 * contentDetector.js's transform math.
 */
function itemGeometry(item, pageHeight) {
  const tx = item.transform;
  const fontSize = Math.hypot(tx[2], tx[3]) || Math.hypot(tx[0], tx[1]) || 12;
  const x = tx[4];
  const yBottom = tx[5];
  const y = pageHeight - yBottom - fontSize;
  const width = item.width || (item.str.length * fontSize * 0.6);
  const height = fontSize * 1.2;
  return { x, y, width, height };
}

/**
 * Drop find matches that fall inside a pending deletion.
 *
 * Search indexes the loaded document bytes directly (see module doc above),
 * independent of the edit layer — so a match whose text lives inside a
 * detected block the user just deleted (tombstoned: the edit survives with
 * `deleted: true` so save can paint a whiteout over it, see useEdits'
 * hasBakedDrawing) still surfaces as a hit even though the user no longer
 * sees that text on the page.
 *
 * A match is dropped when its rect CENTER falls inside any deleted region
 * rect on the same page. Center-point (not full overlap) keeps this cheap
 * and avoids false drops for matches that merely graze a region's edge.
 * Boundary is exclusive (a center exactly on an edge is NOT considered
 * inside) — deterministic and simple, and false negatives here just mean an
 * edge-case match survives, which is the safe direction to err.
 *
 * @param {Array<{pageNum:number, rect:{x,y,width,height}, id:string}>} matches
 * @param {Map<number, Array<{x,y,width,height}>>} deletedRegionsByPage
 * @returns {Array} filtered matches (new array; input is not mutated)
 */
export function filterMatchesByDeletedRegions(matches, deletedRegionsByPage) {
  if (!deletedRegionsByPage || deletedRegionsByPage.size === 0) return matches;

  return matches.filter((match) => {
    const regions = deletedRegionsByPage.get(match.pageNum);
    if (!regions || regions.length === 0) return true;

    const cx = match.rect.x + match.rect.width / 2;
    const cy = match.rect.y + match.rect.height / 2;

    const insideAny = regions.some(
      (r) => cx > r.x && cx < r.x + r.width && cy > r.y && cy < r.y + r.height
    );
    return !insideAny;
  });
}

/**
 * createDocumentSearch(pdfDoc) → { search(query, opts), cancel() }
 *
 * search() returns an AsyncGenerator yielding, in page order,
 * { pageNum, matches: [{ itemIndex, start, length, rect }] } for every page
 * that has at least one match. Pages with zero matches are skipped (no empty
 * yields). A per-document page-text cache means re-searching the same
 * pdfDoc never re-calls getTextContent for a page already read.
 */
export function createDocumentSearch(pdfDoc, { getPageGeometry } = {}) {
  const pageCache = new Map(); // pageNum -> { items, pageHeight }
  let cancelled = false;
  let generation = 0;

  function cancel() {
    cancelled = true;
    generation += 1; // invalidate any in-flight generator's stale checks
  }

  async function getPageData(pageNum) {
    if (pageCache.has(pageNum)) return pageCache.get(pageNum);
    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1 });
    const textContent = await page.getTextContent();
    const items = (textContent.items || []).filter((it) => it.str);
    const data = { items, pageHeight: getPageGeometry?.(pageNum)?.baseHeight || viewport.height };
    pageCache.set(pageNum, data);
    return data;
  }

  async function* search(query, { matchCase = false, edits = [] } = {}) {
    const myGeneration = generation;
    cancelled = false;

    const trimmed = (query || '').trim();
    if (trimmed.length < 2) return;

    const needle = matchCase ? trimmed : normalize(trimmed);
    const numPages = pdfDoc.numPages;

    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      if (cancelled || generation !== myGeneration) return;

      const { items, pageHeight } = await getPageData(pageNum);
      if (cancelled || generation !== myGeneration) return;

      const matches = [];
      for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
        const item = items[itemIndex];
        const str = item.str;
        if (!str) continue;
        // Normalize per-item (not the whole page) to keep offset↔item
        // mapping simple — a per-item normalized string has the same length
        // relationship as the original for our (mostly ASCII) use case, so
        // offsets found in the normalized string map directly back onto the
        // original item's character positions.
        const haystack = matchCase ? str : normalize(str);
        const offsets = findAllOffsets(haystack, needle);
        if (offsets.length === 0) continue;

        const geom = itemGeometry(item, pageHeight);
        for (const start of offsets) {
          const length = needle.length;
          const rectX = geom.x + (start / str.length) * geom.width;
          const rectWidth = (length / str.length) * geom.width;
          matches.push({
            itemIndex,
            start,
            length,
            rect: { x: rectX, y: geom.y, width: rectWidth, height: geom.height },
          });
        }
      }

      const pageEdits = edits.filter((edit) => edit.pageNum === pageNum && edit.type === 'text');
      const replaced = pageEdits.filter((edit) => edit.modified || edit.deleted || edit.restored && !edit.apCarried)
        .map((edit) => edit.originalBounds || edit.bakedBounds).filter(Boolean);
      const visible = filterMatchesByDeletedRegions(matches.map((match) => ({ ...match, pageNum })), new Map([[pageNum, replaced]]));
      // PDF text extraction excludes editable annotation appearances. Search
      // the live overlay values as well, including newly typed replacement text.
      for (const edit of pageEdits) {
        if (edit.deleted || edit.source === 'detected' && !edit.modified && !edit.restored) continue;
        const lines = (edit.content || '').split('\n');
        lines.forEach((line, lineIndex) => {
          const offsets = findAllOffsets(matchCase ? line : normalize(line), needle);
          const height = (Number(edit.styles?.fontSize) || 14) * (Number(edit.styles?.lineHeight) || 1.3);
          const width = Math.min(edit.width, line.length * (Number(edit.styles?.fontSize) || 14) * .6);
          for (const start of offsets) visible.push({ pageNum, start, length: needle.length,
            rect: { x: edit.x + start / line.length * width, y: edit.y + lineIndex * height, width: needle.length / line.length * width, height } });
        });
      }
      if (visible.length > 0) {
        yield { pageNum, matches: visible };
      }

      // Chunk pages with an await between so the UI thread breathes on large
      // documents (macrotask yield, not just a microtask).
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }

  return { search, cancel };
}
